import { useState, useMemo } from 'react';
import {
  Paper,
  TextField,
  InputAdornment,
  Box,
  TablePagination,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from '../../utils/i18n';

const headerStyle = {
  backgroundColor: '#485D9A',
  color: 'white',
  border: '1px solid #ddd',
  padding: '12px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};
const headerStyleDate = {
  backgroundColor: '#485D9A',
  color: 'white',
  border: '1px solid #ddd',
  padding: '12px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  minWidth: '200px'
};

const getStickyStyle = (leftPos, z, bgColor) => ({
  position: 'sticky',
  left: leftPos,
  zIndex: z,
  backgroundColor: bgColor
});

const cellStyle = {
  border: '1px solid #e0e0e0',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'center'
};

const cellStyleDate = {
  border: '1px solid #e0e0e0',
  minWidth: '200px',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'center'
};

const DataTableDailyFuelSummary = ({ rows = [], loading = false, fuelSource = 'sensor' }) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [searchBy, setSearchBy] = useState('vehicle');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getRowData = (row) => {
    const tank = Number(row.fuel_tank) || 0;
    const arr = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];

    // Start Volume
    const startVol = arr.length > 0 ? Number(arr[0].start_fuel_level) || 0 : 0;
    const startPct = tank > 0 ? ((startVol / tank) * 100).toFixed(2) + '%' : '0.0%';

    // End Volume
    const endVol = arr.length > 0 ? Number(arr[arr.length - 1].end_fuel_level) || 0 : 0;
    const endPct = tank > 0 ? ((endVol / tank) * 100).toFixed(2) + '%' : '0.0%';

    // Refueling
    const refuelVol = arr.reduce((sum, item) => sum + (Number(item.rest_fuel_level) || 0), 0);
    const refuelPct = tank > 0 ? ((refuelVol / tank) * 100).toFixed(2) + '%' : '0.0%';

    // Canbus
    const canbusStart = Number(row.start_fuel_consumed_canbus) || 0;
    const canbusEnd = Number(row.end_fuel_consumed_canbus) || 0;
    const canbusTotal = Number(row.total_fuel_consumed_canbus) || 0;

    // Actual
    const eff = Number(row.km_to_litre) || 0;
    const effCanbus = Number(row.km_to_litre_canbus) || 0;
    const actualSensor = eff.toFixed(2);
    const actualCanbus = effCanbus.toFixed(2);

    return {
      date: row.date || '-',
      license_plate: row.license_plate || '-',
      vehicle_type_name: row.vehicle_type_name || '-',
      startPct,
      startVol,
      endPct,
      endVol,
      refuelPct,
      refuelVol,
      canbusStart,
      canbusEnd,
      canbusTotal,
      distance: row.distance || 0,
      total_engine_on: row.total_engine_on || '00:00:00',
      idle_time: row.idle_time || '00:00:00',
      actualSensor,
      actualCanbus
    };
  };

  const formattedRows = useMemo(() => {
    return rows.map((row, index) => ({
      no: index + 1,
      original: row,
      ...getRowData(row)
    }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!search) return formattedRows;
    const lowerSearch = search.toLowerCase();
    return formattedRows.filter((row) => {
      if (searchBy === 'vehicle') {
        return String(row.license_plate || '')
          .toLowerCase()
          .includes(lowerSearch);
      } else {
        return String(row.vehicle_type_name || '')
          .toLowerCase()
          .includes(lowerSearch);
      }
    });
  }, [formattedRows, search, searchBy]);

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
      <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight="bold">
          {t('dailyFuel.dailyFuelSummary', 'Daily Fuel Summary')}
        </Typography>
        <Box display="flex" gap={1}>
          <FormControl size="small">
            <Select
              value={searchBy}
              onChange={(e) => {
                setSearchBy(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="vehicle">{t('dashboardAsset.licensePlate', 'License Plate')}</MenuItem>
              <MenuItem value="type">{t('general.vehicleType', 'Vehicle Type')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search local..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 600, border: '1px solid #ddd', borderRadius: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1500 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              {/* 1st Row */}
              <th
                rowSpan={3}
                style={{ ...headerStyle, ...getStickyStyle(0, 4, '#485D9A'), minWidth: 50, maxWidth: 50, boxSizing: 'border-box' }}
              >
                {t('dataTable.no', 'No')}
              </th>
              <th
                rowSpan={3}
                style={{ ...headerStyleDate, ...getStickyStyle(50, 4, '#485D9A'), minWidth: 200, maxWidth: 200, boxSizing: 'border-box' }}
              >
                {t('general.date', 'Date')}
              </th>
              <th
                rowSpan={3}
                style={{ ...headerStyle, ...getStickyStyle(250, 4, '#485D9A'), minWidth: 150, maxWidth: 150, boxSizing: 'border-box' }}
              >
                {t('dashboardAsset.licensePlate', 'License Plate')}
              </th>
              <th rowSpan={3} style={headerStyle}>
                {t('general.vehicleType', 'Vehicle Type')}
              </th>
              {(fuelSource === 'sensor' || fuelSource === 'all') && (
                <th colSpan={6} style={headerStyle}>
                  {t('dailyFuel.fuelByCalculateSensor', 'Fuel By Calculate Sensor')}
                </th>
              )}
              {(fuelSource === 'canbus' || fuelSource === 'all') && (
                <th colSpan={3} rowSpan={2} style={headerStyle}>
                  {t('dailyFuel.fuelConsumption', 'Fuel Consumption')} (Canbus)
                </th>
              )}
              <th rowSpan={3} style={headerStyle}>
                {t('dailyFuel.totalMileage', 'Total Mileage')} (KM)
              </th>
              <th rowSpan={3} style={headerStyle}>
                {t('dailyFuel.travelingTime', 'Traveling Time')}
              </th>
              <th rowSpan={3} style={headerStyle}>
                {t('dailyFuel.idleTime', 'Idle Time')}
              </th>
              <th rowSpan={3} style={headerStyle}>
                {t('dailyFuel.actual', 'Actual')} (KM/L)
              </th>
            </tr>
            <tr>
              {/* 2nd Row */}
              {(fuelSource === 'sensor' || fuelSource === 'all') && (
                <>
                  <th colSpan={2} style={headerStyle}>
                    {t('dailyFuel.startVol', 'Start Volume')}
                  </th>
                  <th colSpan={2} style={headerStyle}>
                    {t('dailyFuel.endVol', 'End Volume')}
                  </th>
                  <th colSpan={2} style={headerStyle}>
                    Est. {t('dailyFuel.refueling', 'Refueling')} (L)
                  </th>
                </>
              )}
            </tr>
            <tr>
              {/* 3rd Row */}
              {(fuelSource === 'sensor' || fuelSource === 'all') && (
                <>
                  <th style={headerStyle}>%</th>
                  <th style={headerStyle}>{t('dailyFuel.vol', 'Vol')} (L)</th>
                  <th style={headerStyle}>%</th>
                  <th style={headerStyle}>{t('dailyFuel.vol', 'Vol')} (L)</th>
                  <th style={headerStyle}>%</th>
                  <th style={headerStyle}>{t('dailyFuel.vol', 'Vol')} (L)</th>
                </>
              )}

              {(fuelSource === 'canbus' || fuelSource === 'all') && (
                <>
                  <th style={headerStyle}>{t('dailyFuel.startVol', 'Start Volume')} (L)</th>
                  <th style={headerStyle}>{t('dailyFuel.endVol', 'End Volume')} (L)</th>
                  <th style={headerStyle}>{t('dailyFuel.fuelConsumption', 'Fuel Consumption')} (L)</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={16} style={{ ...cellStyle, padding: '20px' }}>
                  <CircularProgress />
                </td>
              </tr>
            ) : paginatedRows.length > 0 ? (
              paginatedRows.map((row) => (
                <tr key={row.no}>
                  <td style={{ ...cellStyle, ...getStickyStyle(0, 1, '#fff'), minWidth: 50, maxWidth: 50, boxSizing: 'border-box' }}>
                    {row.no}
                  </td>
                  <td style={{ ...cellStyleDate, ...getStickyStyle(50, 1, '#fff'), minWidth: 200, maxWidth: 200, boxSizing: 'border-box' }}>
                    {row.date}
                  </td>
                  <td style={{ ...cellStyle, ...getStickyStyle(250, 1, '#fff'), minWidth: 150, maxWidth: 150, boxSizing: 'border-box' }}>
                    {row.license_plate}
                  </td>
                  <td style={cellStyle}>{row.vehicle_type_name}</td>

                  {(fuelSource === 'sensor' || fuelSource === 'all') && (
                    <>
                      <td style={cellStyle}>{row.startPct}</td>
                      <td style={cellStyle}>{row.startVol}</td>
                      <td style={cellStyle}>{row.endPct}</td>
                      <td style={cellStyle}>{row.endVol}</td>
                      <td style={cellStyle}>{row.refuelPct}</td>
                      <td style={cellStyle}>{row.refuelVol.toFixed(2)}</td>
                    </>
                  )}

                  {(fuelSource === 'canbus' || fuelSource === 'all') && (
                    <>
                      <td style={cellStyle}>{row.canbusStart}</td>
                      <td style={cellStyle}>{row.canbusEnd}</td>
                      <td style={cellStyle}>{row.canbusTotal}</td>
                    </>
                  )}

                  <td style={cellStyle}>{row.distance}</td>
                  <td style={cellStyle}>{row.total_engine_on}</td>
                  <td style={cellStyle}>{row.idle_time}</td>
                  <td style={cellStyle}>{fuelSource === 'sensor' ? row.actualSensor : row.actualCanbus}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={16} style={{ ...cellStyle, padding: '20px' }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default DataTableDailyFuelSummary;
